import React, { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { DateModel } from '../../types';
import { isDateFilled, getDateFromModel } from '../../services';
import {
  BaseFieldProps,
  FloatingLabelWrapper,
  FloatingLabelState,
  Text,
} from '@tradingblock/components';

interface DateFieldProps extends BaseFieldProps { }

/** Convert DateModel to YYYY-MM-DD for native date input */
const toDateInputValue = (value: DateModel | null | undefined): string => {
  const d = getDateFromModel(value as DateModel | undefined);
  return d ? d.format('YYYY-MM-DD') : '';
};

/** Parse YYYY-MM-DD string into DateModel */
const parseDateInputValue = (value: string): DateModel | undefined => {
  if (!value || value.length < 10) return undefined;
  const d = dayjs(value);
  if (!d.isValid()) return undefined;
  return {
    month: d.month() + 1,
    day: d.date(),
    year: d.year(),
  };
};

export const DateField: React.FC<DateFieldProps> = ({
  form,
  id,
  disabled,
  defaultLabelId,
  controlledLabel,
  onLabelStateChange,
  errorAfterSubmit,
}) => {
  const { values, touched, errors, setFieldValue, setFieldTouched } = form;
  const value = useMemo(() => _.get(values, id) as DateModel | undefined, [values, id]);
  const error = _.get(touched, id) ? _.pickBy(errors, (err, key) => err && _.startsWith(key, id)) : undefined;
  const forPastDates = useMemo(() => _.endsWith(id, 'dateOfBirth') || _.endsWith(id, 'CreatedDate'), [id]);
  const [floatingLabelState, setFloatingLabelState] = useState<FloatingLabelState>({ isFilled: isDateFilled(value) });
  const updateFloatingLabelState = useCallback(
    (state: FloatingLabelState) => {
      setFloatingLabelState(state);
      if (onLabelStateChange) {
        onLabelStateChange(state);
      }
    },
    [onLabelStateChange]
  );

  // showError will only show errors when field is touched or after submit if errorAfterSubmit is true
  const showError = errorAfterSubmit ? !!form.submitCount : !!_.get(touched, id);

  useEffect(() => {
    if (!controlledLabel) {
      updateFloatingLabelState({ isFilled: isDateFilled(value), hasFocus: floatingLabelState.hasFocus });
    }
  }, [value, controlledLabel, floatingLabelState.hasFocus, updateFloatingLabelState]);

  const dateInputValue = useMemo(() => toDateInputValue(value), [value]);

  /** While focused, mirror the native input so we do not clear the form on partial input (avoids scrambled digits). */
  const [pendingInput, setPendingInput] = useState<string | null>(null);
  const inputValue = pendingInput !== null ? pendingInput : dateInputValue;

  const minDate = useMemo(() => {
    if (forPastDates) return '1900-01-01';
    return dayjs().format('YYYY-MM-DD');
  }, [forPastDates]);

  const maxDate = useMemo(() => {
    if (forPastDates) return dayjs().format('YYYY-MM-DD');
    return '2030-12-31';
  }, [forPastDates]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setPendingInput(v);
      const parsed = parseDateInputValue(v);
      if (parsed) {
        setFieldValue(id, parsed);
      } else if (v === '') {
        setFieldValue(id, { month: undefined, day: undefined, year: undefined });
      }
      updateFloatingLabelState({ isFilled: !!v, hasFocus: true });
    },
    [id, setFieldValue, updateFloatingLabelState]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.currentTarget.value;
      setPendingInput(null);
      setFieldTouched(`${id}.month`, true);
      setFieldTouched(`${id}.day`, true);
      setFieldTouched(`${id}.year`, true);
      const parsed = parseDateInputValue(v);
      if (parsed) {
        setFieldValue(id, parsed);
      } else if (v === '') {
        setFieldValue(id, { month: undefined, day: undefined, year: undefined });
      }
      updateFloatingLabelState({
        isFilled: parsed !== undefined || (v !== '' && isDateFilled(value)),
        hasFocus: false,
      });
    },
    [id, setFieldTouched, value, updateFloatingLabelState, setFieldValue]
  );

  const handleFocus = useCallback(() => {
    setPendingInput(dateInputValue);
    updateFloatingLabelState({ isFilled: true, hasFocus: true });
  }, [dateInputValue, updateFloatingLabelState]);

  const inputId = `date-input-${id.replace(/[.[\]]/g, '-')}`;

  return (
    <>
      <FloatingLabelWrapper
        className={`date-input-single ${!_.isEmpty(error) ? 'invalid' : ''}`}
        state={floatingLabelState}
        disabled={disabled}
      >
        {!floatingLabelState.isFilled && !floatingLabelState.hasFocus && (
          <label className="flabel-title" htmlFor={inputId}>
            <Text
              id={defaultLabelId ? `defaults.${defaultLabelId}` : id}
              textKey="label"
              type="field"
            />
          </label>
        )}
        <div className="flabel-value">
          <input
            id={inputId}
            type="date"
            className="flabel-input date-input-native"
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            min={minDate}
            max={maxDate}
            aria-invalid={!_.isEmpty(error)}
            aria-describedby={!_.isEmpty(error) && showError ? `${inputId}-error` : undefined}
          />
          <i className="far fa-chevron-down date-input-chevron" aria-hidden="true" />
        </div>
      </FloatingLabelWrapper>
      {!_.isEmpty(error) && showError && (
        <span id={`${inputId}-error`} className="error">
          <Text id={id} textKey={'error'} type={'error'} />
        </span>
      )}
    </>
  );
};
